import React from 'react';
import styled from 'styled-components';

import {
  formatSessionDateOrCountdown,
  getSessionStatusBackground,
  getSessionStatusColorVar,
  MarketSessionDisplayStatus,
  MarketSessionInfo,
} from '#/components/markets/intermittent/intermittentMarkets';
import { UnreachableCaseError } from '#/lib/utils';

import {
  MarketSessionDisplay,
  useIntermittentMarketSessionDisplay,
} from './useIntermittentMarketSessionDisplay';

const Badge = styled.span<{
  $status: MarketSessionDisplayStatus;
  $transitionVeryClose?: boolean;
}>`
  display: inline-block;
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 2px;
  font-family: var(--standardFontFamily);
  font-size: var(--small);
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  color: ${({ $status, $transitionVeryClose }) =>
    getSessionStatusColorVar($status, $transitionVeryClose)};
  background: ${({ $status, $transitionVeryClose }) =>
    getSessionStatusBackground($status, $transitionVeryClose)};
`;

interface Props {
  sessionInfo?: MarketSessionInfo;
}

/**
 * Per row market status badge for the markets table. Intermittent markets only:
 * "Next Close: 59:23" mm:ss countdown (≤60m to close), "Closed",
 * "Next Open: 59:23" mm:ss countdown (≤60m to open) and
 * "Close Pending" / "Open Pending" while a transition awaits engine
 * confirmation. Nothing is rendered while open with no close within an hour.
 * Colors match the BuySell panel session banner: green while open, red while
 * closed, yellow within 5 min of a transition and while a transition is pending.
 */
export default function MarketSessionBadge({ sessionInfo }: Props) {
  const session = useIntermittentMarketSessionDisplay(
    sessionInfo,
    'every-tick'
  );
  if (!session) {
    return null;
  }
  return getBadge(session);
}

function getBadge(session: MarketSessionDisplay) {
  const badgeProps = {
    $status: session.status,
    $transitionVeryClose: session.transitionVeryClose,
  };
  switch (session.status) {
    case 'open':
      if (!session.transitionInLessThan1Hour) {
        return null;
      }
      return (
        <Badge {...badgeProps}>
          Next Close:{' '}
          {formatSessionDateOrCountdown(
            session.nextTransitionMs,
            session.nowMs
          )}
        </Badge>
      );
    case 'closed':
      if (!session.transitionInLessThan1Hour) {
        return <Badge {...badgeProps}>Closed</Badge>;
      }
      return (
        <Badge {...badgeProps}>
          Next Open:{' '}
          {formatSessionDateOrCountdown(
            session.nextTransitionMs,
            session.nowMs
          )}
        </Badge>
      );
    case 'closing':
      return <Badge {...badgeProps}>Close Pending</Badge>;
    case 'opening':
      return <Badge {...badgeProps}>Open Pending</Badge>;
    default: {
      throw new UnreachableCaseError(session.status);
    }
  }
}
